/* eslint-disable react/no-unescaped-entities */
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { getFeaturedNewsAndEvents } from '../../services/news';
import Carousel from '../../components/molecules/carousel/carousel';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin-bottom: 15px;
    text-align: justify;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

const links = {
  showroom: 'https://my.matterport.com/show/?m=Boe2AFEuzJe',
  dualRefSystemArticle: 'https://subzero-wolf.com.ph/news-and-events/26659',
  inductionArticle: 'https://subzero-wolf.com.ph/news-and-events/26657',
  bookAnAppointment:
    'https://calendly.com/fgi-kitchen-bookings?utm_source=Instagram&utm_medium=Bio_Link&utm_campaign=Calendly_Appointments&utm_content=SZWF'
};

const carouselImages = [
  `${strapiAssetBaseUrl}/1_8be5194c8b.jpg`,
  `${strapiAssetBaseUrl}/2_977948eced.jpg`,
  `${strapiAssetBaseUrl}/3_59d0a4be34.jpg`,
  `${strapiAssetBaseUrl}/4_1ec8be0b65.jpg`
];

function ArticleContent() {
  return (
    <article className="flex flex-col gap-4">
      <h1>SUMMER IN THE HAMPTONS</h1>
      <p>
        Experience the best of luxe summer living with Sub-Zero and Wolf's first
        cooking demo for the year
      </p>
      <Carousel
        imageURLs={carouselImages}
        name="Summer in the Hamptons article"
      />

      <p>
        May 20, 2023 - Celebrating the last sunny days of the summer in the
        pinnacle of luxe and leisure--Sub-Zero and Wolf transported guests to
        the lavish yet relaxed seaside town of the Hamptons. This cooking demo
        was conducted in the{' '}
        <Link href={links.showroom}>
          brands' exclusive showroom at The Residences at Greenbelt
        </Link>{' '}
        in Makati City. Focus Global's corporate chefs amazed guests by creating
        show stopping Hamptons-inspired dishes bringing the ultimate summer
        experience.
      </p>

      <div className="flex gap-2 mb-4 justify-center">
        <div className="h-80 w-48 relative">
          <Image
            alt="Table Decor 2"
            className="object-contain"
            layout="fill"
            src={`${strapiAssetBaseUrl}/5_ea96b9dd76.jpg`}
          />
        </div>
        <div className="h-80 w-48 relative">
          <Image
            alt="Burrata Mesclun Greens"
            className="object-contain"
            layout="fill"
            src={`${strapiAssetBaseUrl}/6_6a19b753ea.jpg`}
          />
        </div>
      </div>

      <p>
        Enchanting the senses from the very first bite, the culinary journey
        commenced with a dish that epitomized Hamptons’ elegance: Baked Burrata
        in Phyllo with Mesclun Greens. This light and refreshing appetizer
        awakened the summer afternoon with the soft, oozing burrata enveloped in
        the crisp golden phyllo pastry and served on a vibrant bed of fresh
        mesclun greens drizzled with raspberry vinaigrette.{' '}
        <Link href={links.dualRefSystemArticle}>
          Sub-Zero’s Dual Refrigeration System
        </Link>{' '}
        ensured a moist and chilly environment for the burrata cheese for
        optimal preservation. While the Wolf M Series Oven's Convection Mode
        perfectly browned the exterior of the delicate phyllo pastry.
      </p>

      <div>
        <div className="md:float-left mb-6 md:mb-0 md:mr-3">
          <Image
            alt="Article Image 7"
            className="object-contain object-center"
            height={270}
            src={`${strapiAssetBaseUrl}/7_2cbfd0dad9.jpg`}
            width={480}
          />
        </div>
        <p className="text-justify">
          Wolf's Convection Steam Oven (CSO), a versatile 3-in-1 appliance that
          uses steam, convection, or a combination of the two, impressed the
          guests with the creation of the next dish - Maine Lobster Roll and
          Brioche Buns - completely from scratch. From steaming to baking, the
          Wolf CSO allows effortless cooking with its different modes. To start,
          Focus Global corporate chefs utilized steam mode to perfectly cook 2
          kilograms of lobsters in just 5 minutes! They shared that steaming
          with the CSO is not just faster and safer, but its gentle heat helps
          preserve the flavor and tenderness of this delectable shellfish.
          Beyond steaming, the CSO also features convection steam mode, which is
          ideal for baking handcrafted brioche buns. By combining wet heat and
          dry heat, these buns came out with a soft, fluffy interior. While the
          exterior had a glossy, golden brown color even without an egg wash.
        </p>
      </div>

      <div>
        <div className="md:float-right mb-6 md:mb-0 md:ml-3">
          <Image
            alt="Article Image 8"
            className="object-contain object-center"
            height={270}
            src={`${strapiAssetBaseUrl}/8_2371f62e2f.jpg`}
            width={480}
          />
        </div>
        <p className="text-justify">
          Next on the menu is the Beef Tenderloin Roulade with Orzo Salad - this
          succulent beef tenderloin is stuffed with goat cheese and pear served
          alongside a light and flavorful orzo salad. This appetizing entree was
          made with the Wolf M Series Oven using the Convection Roast Mode, a
          high-powered roasting method that produces intense and even browning,
          perfect for your meats. With the temperature probe, the chefs were
          able to monitor the beef tenderloin’s internal temperature for a
          medium rare finish (135°F) without the need to interrupt the cooking
          process and open the oven door. A typical meat thermometer would
          normally require this, but since Wolf's temperature probe is connected
          to the appliance itself, the oven is smart enough to know when a dish
          has been cooked to perfection — precisely achieving the desired level
          of doneness. For the perfect accompaniment to the beef tenderloin, the
          orzo salad was prepared with the Wolf Induction Cooktop. Focus Global
          corporate chefs were able to showcase instantaneous adjustment
          response - achieving rapid high heat to boil the chicken stock with
          the Boost Mode and transitioning to gentle, low heat using True Simmer
          Mode to finish the cooking process. The Orzo Salad achieved the
          perfect texture on the Wolf Induction Cooktop that heats up through{' '}
          <Link href={links.inductionArticle}>electromagnetism</Link> – directly
          heating your cooking vessel and ensuring precise temperature.
        </p>
      </div>

      <div>
        <div className="md:float-left mb-6 md:mb-0 md:mr-3">
          <Image
            alt="Article Image 9"
            className="object-contain object-center"
            height={270}
            src={`${strapiAssetBaseUrl}/9_9e45161305.jpg`}
            width={480}
          />
        </div>
        <p className="text-justify">
          The afternoon was capped off with a Triple Berry Pie with Vanilla Ice
          Cream paired with an Iced Lavender Oat Latte that radiated summer
          sophistication. In only a few touches, the Gourmet Mode on the Wolf M
          Series Oven allowed for the Triple Berry Pie to bake to perfection as
          it decided the time, temperature and rack positioning of the dish.
          This pie was topped with a scoop of creamy vanilla ice cream. A
          surprising but refreshing pairing for this pie was the Iced Lavender
          Oat Latte created with the Wolf Coffee System. This drink is
          reminiscent of a meticulously prepared barista drink made in the
          comfort of your own kitchen.
        </p>
      </div>

      <div className="flex w-full gap-2 justify-center mb-4">
        <Image
          alt="Article Image 10"
          className="object-contain"
          height={640}
          src={`${strapiAssetBaseUrl}/10_6c59d6326c.jpg`}
          width={1152}
        />
        <Image
          alt="Oat Lavender Latte"
          className="object-contain"
          height={640}
          src={`${strapiAssetBaseUrl}/11_cfad35b556.jpg`}
          width={360}
        />
      </div>

      <p>
        Embark on your next culinary destination and{' '}
        <Link href={links.bookAnAppointment}>book an appointment</Link> with us
        to learn more about Sub-Zero and Wolf.
      </p>

      <Image
        alt="Cooking Demo with Audience"
        height={540}
        src={`${strapiAssetBaseUrl}/12_2b7c1482cc.jpg`}
        width={960}
      />
    </article>
  );
}

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ArticleContent />
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps() {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = {
    attributes: {
      slug: '26801',
      news_title: 'Summer in the Hamptons'
    }
  } as NewsAndEventsModel;

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export default Index;
